import { effectiveDeviceRole, isTwoInningsMatch } from "./device-session.js";

export const DEVICE_SESSION_KEY = "cs_device_session_v1";
const LIVE_SCREENS = new Set(["scoring", "recording", "break", "result"]);
const MATCH_CREATION_SCREENS = new Set(["setup", "players", "recorder-join"]);
const VALID_ROLES = new Set(["scorer", "recorder", "single_score", "single_record"]);

function storageOrDefault(storage) {
  return storage || globalThis.localStorage;
}

export function saveDeviceSession(session, storage) {
  const target = storageOrDefault(storage);
  if (!target || !VALID_ROLES.has(session?.role)) return false;
  const value = {
    role: session.role,
    roomCode: /^\d{6}$/.test(String(session.roomCode || "")) ? String(session.roomCode) : null,
    screen: typeof session.screen === "string" ? session.screen : null
  };
  target.setItem(DEVICE_SESSION_KEY, JSON.stringify(value));
  return true;
}

export function loadDeviceSession(storage) {
  const target = storageOrDefault(storage);
  if (!target) return null;
  try {
    const value = JSON.parse(target.getItem(DEVICE_SESSION_KEY) || "null");
    if (!VALID_ROLES.has(value?.role)) return null;
    return {
      role: value.role,
      roomCode: /^\d{6}$/.test(String(value.roomCode || "")) ? String(value.roomCode) : null,
      screen: typeof value.screen === "string" ? value.screen : null
    };
  } catch (_) {
    return null;
  }
}

export function clearDeviceSession(storage) {
  storageOrDefault(storage)?.removeItem(DEVICE_SESSION_KEY);
}

function requestDone(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error || new Error("IndexedDB request failed"));
  });
}

export async function deleteActiveMatchRecords(db, matchId) {
  if (!db) return;
  const activeRequest = db.transaction("active", "readwrite").objectStore("active").delete("current");
  const tasks = [requestDone(activeRequest)];
  if (matchId) {
    const checkpointRequest = db.transaction("checkpoints", "readwrite").objectStore("checkpoints").delete(matchId);
    tasks.push(requestDone(checkpointRequest));
  }
  await Promise.all(tasks);
}

export function selectInitialScreen({ requested = "home", match = null, role = null, inningsOver = false } = {}) {
  if (!match) return requested;
  const effectiveRole = role || effectiveDeviceRole(match, null);
  const liveScreen = effectiveRole === "recorder" || effectiveRole === "single_record" ? "recording" : "scoring";
  const chase = isTwoInningsMatch(match) && match.innings?.length === 2
    ? match.innings[1]
    : null;
  const chaseNeedsOpeners = !!chase &&
    Number(chase.balls || 0) === 0 &&
    (!chase.currentStriker || !chase.currentBowler);
  const resumeScreen = match.result
    ? "result"
    : chaseNeedsOpeners && effectiveRole !== "recorder"
      ? "break"
    : isTwoInningsMatch(match) && match.innings?.length === 1 && inningsOver
      ? (liveScreen === "recording" ? "recording" : "break")
      : liveScreen;

  if (MATCH_CREATION_SCREENS.has(requested)) return resumeScreen;
  if (!LIVE_SCREENS.has(requested)) return requested;
  return resumeScreen;
}
